import React, { useState, useMemo, useEffect } from "react";
import { useCfmContext } from "../../../Context/CfmContext";
import AddIcon from "../../Buttons/AddIcon";
import "../../../../../../css/CashflowModeller2/Tabs/Adhoc.css";
import Input2 from "../../Inputs/Input2";
import Input1 from "../../Inputs/Input1";
import RemoveIcon from "../../Buttons/RemoveIcon";
import AdHocOptions from "../../../Utils/AdHocOptions";
import getPensionInvestmentOptions from "../../../Utils/PensionInvestmentOptions";
import getTotalIsaContribution from "../../../Utils/GetTotalIsaContribution";
import Input2Medium from "../../Inputs/Input2Medium";
import Input1Big from "../../Inputs/Input1Big";

const defaultRow = {
  adHocType: "",
  name: "",
  type: "",
  year: "",
  amount: { value: "", unit: "Annual" },
};

/**
 * AdHoc component for managing ad-hoc contributions and withdrawals in the Cashflow Modeller.
 *
 * This component allows users to add, edit, and remove ad-hoc financial entries (such as contributions or withdrawals)
 * for various investment sources (e.g., ISA, Pension). It enforces validation rules, including ISA contribution limits,
 * and provides real-time feedback for invalid or excessive entries.
 *
 * State and Context:
 * - Uses the Cashflow Modeller context for global state and dispatch.
 * - Manages local state for the current input row and validation errors.
 *
 * Features:
 * - Validates input fields for type, source, year, and amount.
 * - Enforces ISA annual and monthly contribution limits across all sources.
 * - Allows editing and removal of existing ad-hoc entries.
 * - Displays error messages for invalid inputs or exceeded limits.
 *
 * Dependencies:
 * - Relies on helper functions such as `getPensionInvestmentOptions` and `getTotalIsaContribution`.
 * - Uses custom input components (`Input1`, `Input2`), and icon components (`AddIcon`, `RemoveIcon`).
 *
 * @component
 * @returns {JSX.Element} The rendered AdHoc management UI.
 */
const AdHoc = () => {
  const { state, dispatch } = useCfmContext();
  const [inputRow, setInputRow] = useState({ ...defaultRow });
  const [errors, setErrors] = useState({});
  const adHocData = state.adHocData || [];
  const allSourceOptions = getPensionInvestmentOptions(state);

  /**
   * Handles changes to input fields by updating the corresponding value in the input row state
   * and resetting any error state for the changed field.
   *
   * @param {string} field - The name of the input field being changed.
   * @param {*} value - The new value for the input field.
   */
  const handleInputChange = (field, value) => {
    setInputRow((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  /**
   * Handles the addition of a new ad-hoc entry after validating input fields.
   * 
   * Validates the following:
   * - All required fields are present (adHocType, name, year, amount).
   * - Year is a 4-digit number between 1900 and 2100.
   * - Amount is a valid number.
   * - For ISA contributions, ensures the total does not exceed annual or monthly limits.
   * 
   * If validation fails, sets appropriate error messages.
   * If validation passes, dispatches the new entry to the adHocData state and resets the input row and errors.
   * 
   * @function
   * @returns {void}
   */
  const handleAdd = () => {
    const { adHocType, name, year, amount } = inputRow;
    const [fullName, type] = (name || "").split(" - ").map((s) => s.trim());

    const newErrors = {
      adHocType: !adHocType,
      name: !name,
      year:
        !year ||
        isNaN(Number(year)) ||
        year.length !== 4 ||
        Number(year) < 1900 ||
        Number(year) > 2100,
      amount: !amount?.value || isNaN(Number(amount.value)),
    };

    const isValid = !Object.values(newErrors).some(Boolean);

    if (!isValid || !fullName || !type) {
      // console.log("Validation Error: Required fields failed", newErrors);
      setErrors({ ...newErrors });
      return;
    }

    // if (
    //   type === "ISA" &&
    //   ((amount.unit === "Annual" && parseFloat(amount.value) > 20000) ||
    //     (amount.unit === "Monthly" && parseFloat(amount.value) > 1666.67))
    // ) {
    //   const updatedErrors = {
    //     ...errors,
    //     amount: true,
    //     customMessage:
    //       "ISA ad-hoc amount cannot exceed £20,000 annually or £1,666.67 monthly.",
    //   };
    //   // console.log("Validation Error: ISA amount exceeds limits", updatedErrors);
    //   setErrors(updatedErrors);
    //   return;
    // }

    if (type === "ISA" && adHocType === "Contribution") {
      const totalISA = getTotalIsaContribution(
        state.investments,
        state.adHocData,
        amount.unit,
        fullName
      );
      const newValue = parseFloat(amount.value || 0);

      const limit = amount.unit === "Monthly" ? 1666.67 : 20000;
      if (totalISA + newValue > limit && adHocType === "Contribution") {
        setErrors((prev) => ({
          ...prev,
          amount: true,
          customMessage: `Total ISA contributions across all sources cannot exceed £${limit.toLocaleString()} ${amount.unit.toLowerCase()}.`,
        }));
        return;
      }
    }

    const newEntry = {
      adHocType,
      name: fullName,
      type,
      year,
      amount,
    };

    // console.log("Adding ad-hoc entry", newEntry);

    dispatch({ type: "SET_ADHOC_DATA", payload: [...adHocData, newEntry] });
    setInputRow({ ...defaultRow });
    setErrors({});
  };

  /**
   * Removes an item from the adHocData array at the specified index and updates the state.
   *
   * @param {number} index - The index of the item to remove from the adHocData array.
   * @returns {void}
   */
  const handleRemove = (index) => {
    const updated = adHocData.filter((_, i) => i !== index);
    dispatch({ type: "SET_ADHOC_DATA", payload: updated });
  };

  const formattedData = useMemo(() => {
    return adHocData.map((item) => ({
      ...item,
      displayName: `${item.name.split(" ")[0]} - ${item.type}`,
    }));
  }, [adHocData]);

  // useEffect(() => {
  //   console.log(state);
  // }, []);

  useEffect(() => {
    const newErrors = {};

    adHocData.forEach((item, index) => {
      const isISA = item.type === "ISA" && item.adHocType === "Contribution";
      if (!isISA) return;

      const unit = item.amount.unit || "Annual";
      const limit = unit === "Monthly" ? 1666.67 : 20000;
      const currentTotal = getTotalIsaContribution(
        state.investments,
        adHocData.filter((_, i) => i !== index),
        unit,
        item.name

      );
      const parsedVal = parseFloat(item.amount.value || "0");

      if (currentTotal + parsedVal > limit) {
        const maxAllowed = limit - currentTotal;
        newErrors[
          `amount-${index}`
        ] = `You've reached your ISA limit of £${limit.toLocaleString()}. Max allowed here is £${maxAllowed.toLocaleString()}`;
      }
    });

    setErrors(newErrors);
  }, [adHocData, state.investments]);

  return (
    <div className="Adhoc-container">
      <div className="Adhoc-row">
        <Input2Medium
          label="Type"
          type="invType"
          value={inputRow.adHocType}
          onChange={(val) => handleInputChange("adHocType", val)}
          options={AdHocOptions}
          isError={errors.adHocType}
          isDisabled={allSourceOptions.length === 0}
          placeholderText={"Contribution"}
        />
        <Input2Medium
          label="Source"
          type="invType"
          placeholderText="William"
          value={inputRow.name}
          onChange={(val) => handleInputChange("name", val)}
          options={allSourceOptions}
          isError={errors.name}
          isDisabled={allSourceOptions.length === 0}
        />
        <Input1
          label="Year"
          type="customDate"
          value={inputRow.year}
          onChange={(val) => handleInputChange("year", val.slice(0, 4))}
          isError={errors.year}
          isDisabled={allSourceOptions.length === 0}
        />
        <Input1Big
          label="Amount"
          value={inputRow.amount.value}
          onChange={(val) =>
            handleInputChange("amount", { ...inputRow.amount, value: val })
          }
          isError={!!errors.amount}
          type="number"
          currency={true}
          isDisabled={allSourceOptions.length === 0}
        />
        <AddIcon onClick={handleAdd} />
      </div>
      {errors.customMessage && (
        <div className="error Label">{errors.customMessage}</div>
      )}

      {formattedData.map((item, index) => (
        <React.Fragment key={index}>
          {index === 0 && <div className="no-dovider-dialogBox" />}
          <div className="Adhoc-row">
            <Input2Medium
              value={item.adHocType}
              onChange={(val) => {
                const updated = [...adHocData];
                updated[index].adHocType = val;
                dispatch({ type: "SET_ADHOC_DATA", payload: updated });
              }}
              type="invType"
              // options={AdHocOptions}
              readOnly={true}
            />
            <Input2Medium
              type="invType"
              value={item.displayName}
              onChange={(val) => {
                const [fullName, type] = val.split(" - ").map((s) => s.trim());
                const updated = [...adHocData];
                updated[index].name = fullName;
                updated[index].type = type;
                dispatch({ type: "SET_ADHOC_DATA", payload: updated });
              }}
              // options={allSourceOptions}
              readOnly={true}
            />
            <Input1
              type="customDate"
              value={item.year}
              // readOnly
              onChange={(val) => {
                const updated = [...adHocData];
                updated[index].year = val.slice(0, 4);
                dispatch({ type: "SET_ADHOC_DATA", payload: updated });
              }}
              // onChange={() => {}}
            />
            <Input1Big
              value={item.amount.value || ""}
              isError={!!errors[`amount-${index}`]}
              type="number"
              currency={true}
              onChange={(val) => {
                const isISA =
                  item.type === "ISA" && item.adHocType === "Contribution";
                const unit = item.amount.unit || "Annual";
                const limit = unit === "Monthly" ? 1666.67 : 20000;

                const parsedVal = parseFloat(val || "0");
                if (isNaN(parsedVal)) return;

                if (isISA) {
                  const currentTotal = getTotalIsaContribution(
                    state.investments,
                    adHocData.filter((_, i) => i !== index),
                    unit,
                    item.name
                  );

                  const maxAllowed = limit - currentTotal;
                  // console.log(maxAllowed);
                  const clampedValue =
                    parsedVal > maxAllowed ? maxAllowed : parsedVal;

                  // console.log("Clamped Value", clampedValue);

                  if (parsedVal > maxAllowed) {
                    setErrors((prev) => ({
                      ...prev,
                      [`amount-${index}`]: `You've reached your ISA limit of £${limit.toLocaleString()}. Max allowed here is £${(maxAllowed <
                      0
                        ? 0
                        : maxAllowed
                      ).toLocaleString()} `,
                    }));
                  } else {
                    setErrors((prev) => {
                      const copy = { ...prev };
                      delete copy[`amount-${index}`];
                      return copy;
                    });
                    const updated = [...adHocData];
                    updated[index] = {
                      ...updated[index],
                      amount: {
                        ...updated[index].amount,
                        value: clampedValue.toString(),
                      },
                    };
                    dispatch({ type: "SET_ADHOC_DATA", payload: updated });
                  }

                  return;
                }

                // If it's NOT ISA Contribution, just update value and clear any existing errors
                // console.log("Updating non-ISA value", parsedVal);
                const updated = [...adHocData];
                updated[index] = {
                  ...updated[index],
                  amount: {
                    ...updated[index].amount,
                    value: parsedVal.toString(),
                  },
                };

                setErrors((prev) => {
                  const copy = { ...prev };
                  delete copy[`amount-${index}`];
                  return copy;
                });

                dispatch({ type: "SET_ADHOC_DATA", payload: updated });
              }}
            />

            <RemoveIcon onClick={() => handleRemove(index)} />
          </div>
          {errors[`amount-${index}`] && (
            <div className="error Label">{errors[`amount-${index}`]}</div>
          )}
          <div className="no-dovider-dialogBox" />
        </React.Fragment>
      ))}
    </div>
  );
};

export default AdHoc;
